<template>
  <div
    v-if="device"
    class="m-0 h-100 w-100 d-flex flex-column p-1 custom-div-body"
  >
    <!-- Modal edit device
    <b-modal v-model="dialogEdit" title="Editar dispositivo" hide-footer size="lg">
      <edit-device-modal :device="device" :show-interval="showInterval" @close="dialogEdit = false"
        @submit="handleEditSubmit" />
    </b-modal> -->

    <!-- title and buttons -->
    <b-card-header class="p-0 d-flex flex-row justify-content-between">
      <!-- left: title -->
      <b-card-title class>
        <span
          :id="'tooltip-title-' + device.name"
          class="d-flex align-items-center p-0 position-relative"
        >
          <!-- Circular badge -->
          <div :class="{ 'circle-badge-offline': device.alertCounts && device.alertCounts.statusAlerts > 0, 'circle-badge-online': !(device.alertCounts && device.alertCounts.statusAlerts > 0) }" />
          <h5 class="m-0">{{ device.name }}</h5>
          <div v-if="device.alertCounts && (device.alertCounts.statusAlerts || device.alertCounts.userAlerts)" class="d-flex align-items-center ml-1">
            <div v-if="device.alertCounts.statusAlerts" class="position-relative">
              <feather-icon badge-classes="bg-danger" class="text-body" icon="WifiOffIcon" size="18" />
              <b-badge pill variant="danger" class="position-absolute" style="height: 12px; width: 12px; top: 0px; right: -10px; display: flex; align-items: center; justify-content: center; font-size: 0.5em;">
                {{ device.alertCounts.statusAlerts }}
              </b-badge>
            </div>
            <div v-if="device.alertCounts.userAlerts" class="position-relative ml-2">
              <feather-icon badge-classes="bg-danger" class="text-body" icon="BellIcon" size="18" />
              <b-badge pill variant="danger" class="position-absolute" style="height: 12px; width: 12px; top: 0px; right: -10px; display: flex; align-items: center; justify-content: center; font-size: 0.5em;">
                {{ device.alertCounts.userAlerts }}
              </b-badge>
            </div>
          </div>
        </span>
        <b-tooltip
          :target="'tooltip-title-' + device.name"
          :title="'Dispositivo: ' + device.device"
          class="cursor-pointer"
        />
      </b-card-title>
      <!-- right: edit and view details buttons -->
      <b-card-title v-if="device.sensorType !== 'demo'">
        <b-button
          :id="'tooltip-view-' + device.name"
          variant="link"
          :to="'/devices/' + device.id + '/' + device.data.toString().replace(/,/g, '-')"
          class="px-1 d-flex align-items-center"
        >
          <span class="d-none d-lg-flex mr-50">Detalles</span>
          <feather-icon
            icon="MoreHorizontalIcon"
            size="18"
            class="text-secondary d-none d-lg-flex"
          />
          <feather-icon
            icon="InfoIcon"
            size="20"
            class="text-secondary d-lg-none"
          />
        </b-button>
        <b-tooltip
          :target="'tooltip-view-' + device.name"
          :title="device.sensorType === 'demo' ? '' : 'Ver detalles'"
          class="cursor-pointer"
        />
      </b-card-title>
    </b-card-header>
    <!--/ title and buttons -->
    <b-card-body
      :class="{ 'd-flex justify-content-center align-items-center': loading }"
      class="py-1 px-0 custom-card-body"
    >
      <span
        v-if="loading"
        class="font-medium-1"
      >
        <b-spinner variant="primary" />
      </span>

      <div
        v-else
        class="custom"
      >
        <div
          v-if="device.sensorType === 'boolean'"
          class="card-data"
        >
          <div class="d-flex align-items-center">
            <b-form-checkbox
              :checked="device.valueBool"
              name="check-button"
              switch
              inline
              disabled
              class="bigger-checkbox custom-control-primary"
            />
            <p class="ml-md-1 mt-1 device-values">
              <span class="device-value">{{ device.values }}</span>
              <span class="device-unit" v-if="displayUnit">{{ displayUnit }}</span>
            </p>
          </div>
        </div>
        <div v-else-if="device.sensorType === 'gps'">
          <map-gps
            :positions="transformIotData(device.createdAt, device.values)"
            map-full-screen-size="80vh"
          />
        </div>
        <div v-else-if="device.sensorType === 'list'" class="card-data">
          <b-avatar
            size="64"
            :style="{ color: currentListDetails.color, backgroundColor: 'rgba(' + hexToRgb(currentListDetails.color) + ', 0.2)' }"
          >
            <feather-icon :icon="currentListDetails.icon" />
          </b-avatar>
          <p class="device-values">
            <span class="device-value">{{ currentListDetails.text }}</span>
            <span class="device-unit" v-if="displayUnit">{{ displayUnit }}</span>
          </p>
        </div>
        <div
          v-else-if="device.sensorType === 'demo'"
          class="demo-device-container d-flex flex-row h-100 justify-content-center align-items-center ml-2"
        >
          <!-- OEE Availability Chart -->
          <div v-if="isSiteVisible" class="d-flex flex-column align-items-center mb-2 mr-2">
            <!-- Radial Bar Chart for OEE Percentage -->
            <vue-apex-charts
              ref="radialChart"
              type="radialBar"
              height="380"
              :options="oeeRadialChartOptions"
              :series="[oeeAvailabilityPercentage]"
            />

            <!-- Current Voltage Display -->
            <b-badge
              variant="primary"
              pill
              class="d-flex align-items-center px-3 py-2"
              style="font-size: 1.2rem;"
            >
              <feather-icon
                icon="ZapIcon"
                size="26"
                class="mr-75"
                style="width: 18px; height: 18px;"
              />
              <span class="font-weight-bold">{{ device.values }} V</span>
            </b-badge>
          </div>

          <!-- Time-segmented Bar (Timeline Chart) -->
          <div class="d-flex flex-column" style="width: 75%;">
            <div class="d-flex justify-content-between align-items-center">
              <small class="font-weight-bold text-muted" style="font-size: 1.2rem;">Historico del Turno</small>
              <b-badge
                variant="light-secondary"
                pill
                style="font-size: 1.2rem;"
              >
                {{ shiftDuration }}h
              </b-badge>
            </div>

            <!-- Range Bar Chart for Timeline -->
            <vue-apex-charts
              ref="timelineChart"
              type="rangeBar"
              height="200"
              :options="timelineChartOptions"
              :series="timelineChartSeries"
            />

            <!-- Timeline Legend -->
            <div class="d-flex flex-wrap justify-content-center">
              <div class="d-flex align-items-center mr-1 mb-50">
                <span
                  class="rounded-sm mr-50"
                  style="width: 10px; height: 10px; background-color: #28c76f; display: inline-block;"
                />
                <small class="text-muted" style="font-size: 1rem;">Funcionando</small>
              </div>
              <div class="d-flex align-items-center mr-1 mb-50">
                <span
                  class="rounded-sm mr-50"
                  style="width: 10px; height: 10px; background-color: #ff9f43; display: inline-block;"
                />
                <small class="text-muted" style="font-size: 1rem;">Encendida</small>
              </div>
              <div class="d-flex align-items-center mr-1 mb-50">
                <span
                  class="rounded-sm mr-50"
                  style="width: 10px; height: 10px; background-color: #6c757d; display: inline-block;"
                />
                <small class="text-muted" style="font-size: 1rem;">Apagada</small>
              </div>
              <div class="d-flex align-items-center mb-50">
                <span
                  class="rounded-sm mr-50"
                  style="width: 10px; height: 10px; background-color: #ea5455; display: inline-block;"
                />
                <small class="text-muted" style="font-size: 1rem;">Falla</small>
              </div>
            </div>
          </div>
        </div>
        <div
          v-else
          iv
          class="card-data"
        >
          <b-avatar
            size="64"
            :style="{ color: device.color, backgroundColor: 'rgba(' + hexToRgb(device.color) + ', 0.2)' }"
          >
            <feather-icon :icon="device.icon ? device.icon : 'ActivityIcon'" />
          </b-avatar>
          <p class="device-values">
            <span class="device-value">{{ device.values }}</span>
            <span class="device-unit" v-if="displayUnit">{{ device.unitType }}</span>
          </p>
        </div>
      </div>
    </b-card-body>
    <div class="d-flex justify-content-between align-self-center px-2 w-100 text-center text-md-left">
      <p class="mt-1 d-none d-md-block w-100 w-md-auto">
        <feather-icon
          icon="ClockIcon"
          size="14"
        />
        {{ (device.timeInterval % 60 === 0 ? (device.timeInterval / 60) : (device.timeInterval / 60).toFixed(1)) }} min
      </p>
      <p class="mt-1 w-100 w-md-auto">
        {{ $moment.utc(device.createdAt).local().format('DD/MM/YYYY hh:mm:ss a') }}
      </p>
    </div>
  </div>
</template>

<script>
import {
  BCardHeader,
  BCardTitle,
  BCardBody,
  BSpinner,
  BTooltip,
  BButton,
  BModal,
  BFormCheckbox,
  BAvatar,
  BBadge,
} from 'bootstrap-vue'

import VueApexCharts from 'vue-apexcharts'
import store from '@/store'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'
// import EditDeviceModal from '@/views/utils/EditDeviceModal.vue'
import MapGps from '../utils/MapGps.vue'
import moment from 'moment-timezone'

export default {
  components: {
    BCardHeader,
    BCardTitle,
    BCardBody,
    BSpinner,
    BTooltip,
    BButton,
    BModal,
    FeatherIcon,
    // EditDeviceModal,
    MapGps,
    BFormCheckbox,
    BAvatar,
    BBadge,
    VueApexCharts,
  },
  props: {
    device: {
      type: Object,
      default: () => { },
    },
    loading: {
      type: Boolean,
      default: false,
    },
    isSiteVisible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dialogEdit: false,
      showInterval: false,
      chartData: [],
      now: Date.now(),
      intervalId: null,
    }
  },
  computed: {
    isDarkMode() {
      return store.state.appConfig.layout.skin === 'dark'
    },
    displayUnit() {
      return this.device.unitType !== 'N/A' ? this.device.unitType : null;
    },
    shiftDuration() {
      if (this.device.shiftDuration) {
        return this.device.shiftDuration
      }
      if (this.device.oeeConfig?.shiftDuration) {
        return this.device.oeeConfig.shiftDuration
      }

      // ==========================================
      // MOCKUP HARDCODEADO - PARA DEMO
      // ==========================================

      const deviceName = this.device.name?.toLowerCase() || ''
      const deviceId = this.device.id || 0

      if (deviceName.includes('24') || deviceName.includes('continuo') || deviceName.includes('continuous')) {
        return 24
      }

      if (deviceName.includes('12') || deviceId % 2 === 0) {
        return 12
      }

      return 8
    },
    timelineSegments() {
      if (this.device.oeeTimeline?.segments) {
        return this.device.oeeTimeline.segments
      }

      // ! MOCK DATA: OEE TIMELINE SEGMENTS
      if (this.shiftDuration === 24) {
        return [
          {
            label: 'Funcionando', duration: 40, color: '#28c76f', time: '00:00-09:36', status: 'running',
          },
          {
            label: 'Encendida', duration: 8, color: '#ff9f43', time: '09:36-11:31', status: 'idle',
          },
          {
            label: 'Funcionando', duration: 35, color: '#28c76f', time: '11:31-19:55', status: 'running',
          },
          {
            label: 'Apagada', duration: 10, color: '#6c757d', time: '19:55-22:23', status: 'off',
          },
          {
            label: 'Falla', duration: 5, color: '#ea5455', time: '22:23-23:12', status: 'failure',
          },
          {
            label: 'Encendida', duration: 2, color: '#ff9f43', time: '23:12-24:00', status: 'idle',
          },
        ]
      }
      if (this.shiftDuration === 12) {
        return [
          {
            label: 'Funcionando', duration: 7, color: '#28c76f', time: '00:00-05:24', status: 'running',
          },
          {
            label: 'Encendida', duration: 10, color: '#ff9f43', time: '05:24-06:36', status: 'idle',
          },
          {
            label: 'Funcionando', duration: 30, color: '#28c76f', time: '06:36-10:12', status: 'running',
          },
          {
            label: 'Falla', duration: 8, color: '#ea5455', time: '10:12-11:09', status: 'failure',
          },
          {
            label: 'Apagada', duration: 45, color: '#6c757d', time: '11:09-12:00', status: 'off',
          },
        ]
      }
      return [
        {
          label: 'Funcionando', duration: 43, color: '#28c76f', time: '00:00-02:48', status: 'running',
        },
        {
          label: 'Encendida', duration: 10, color: '#ff9f43', time: '02:48-03:36', status: 'idle',
        },
        {
          label: 'Funcionando', duration: 20, color: '#28c76f', time: '03:36-05:36', status: 'running',
        },
        {
          label: 'Falla', duration: 5, color: '#ea5455', time: '05:36-06:14', status: 'failure',
        },
        {
          label: 'Apagada', duration: 12, color: '#6c757d', time: '06:14-07:10', status: 'off',
        },
        {
          label: 'Funcionando', duration: 10, color: '#28c76f', time: '07:10-08:00', status: 'running',
        },
      ]
    },
    // OEE Availability calculations based on voltage (BUSINESS LOGIC)
    oeeAvailabilityPercentage() {
      if (this.device.unitType !== 'V') return 0

      const runningTime = this.timelineSegments
        .filter(seg => seg.status === 'running')
        .reduce((sum, seg) => sum + seg.duration, 0)

      return Math.round(runningTime)
    },
    oeeAvailabilityColor() {
      const voltage = parseFloat(this.device.values) || 0

      if (this.isInFailureState()) {
        return '#ea5455' // Red for failure
      }

      // Voltage-based status (BUSINESS LOGIC)
      if (voltage < 5) {
        return '#6c757d' // Gray - Off/Not available
      }
      if (voltage >= 5 && voltage < 10) {
        return '#ff9f43' // Orange - On but not running/Not available
      }
      return '#28c76f' // Green - Running/Available
    },
    oeeStatusText() {
      const voltage = parseFloat(this.device.values) || 0

      if (this.isInFailureState()) {
        return 'Falla'
      }

      if (voltage < 5) {
        return 'Apagada'
      }
      if (voltage >= 5 && voltage < 10) {
        return 'Encendida'
      }
      return 'Funcionando'
    },
    oeeStatusIcon() {
      const voltage = parseFloat(this.device.values) || 0

      if (this.isInFailureState()) {
        return 'AlertTriangleIcon'
      }

      if (voltage < 5) {
        return 'PowerIcon'
      }
      if (voltage >= 5 && voltage < 10) {
        return 'PauseCircleIcon'
      }
      return 'CheckCircleIcon'
    },
    oeeRadialChartOptions() {
      return {
        chart: {
          type: 'radialBar',
          sparkline: {
            enabled: false,
          },
        },
        plotOptions: {
          radialBar: {
            startAngle: -135,
            endAngle: 135,
            hollow: {
              size: '65%',
            },
            track: {
              background: this.isDarkMode ? '#3b4253' : '#e9ecef',
              strokeWidth: '100%',
            },
            dataLabels: {
              name: {
                show: true,
                offsetY: -15,
                fontSize: '16px',
                color: this.isDarkMode ? '#b4b7bd' : '#6c757d',
                fontWeight: 500,
              },
              value: {
                offsetY: 5,
                fontSize: '40px',
                color: this.isDarkMode ? '#fff' : '#2c3e50',
                fontWeight: 'bold',
                formatter: val => `${val}%`,
              },
            },
          },
        },
        fill: {
          type: 'solid',
          colors: [this.oeeAvailabilityColor],
        },
        stroke: {
          lineCap: 'round',
        },
        labels: ['Disponibilidad'],
        colors: [this.oeeAvailabilityColor],
      }
    },
    timelineChartOptions() {
      return {
        chart: {
          type: 'rangeBar',
          toolbar: {
            show: false,
          },
        },
        plotOptions: {
          bar: {
            horizontal: true,
            distributed: true,
            barHeight: '80%',
            rangeBarGroupRows: false,
          },
        },
        dataLabels: {
          enabled: false,
        },
        xaxis: {
          type: 'datetime',
          labels: {
            datetimeUTC: false,
            format: 'HH:mm',
            style: {
              colors: this.isDarkMode ? '#b4b7bd' : '#6c757d',
              fontSize: '14px',
            },
          },
        },
        yaxis: {
          show: false,
        },
        grid: {
          show: false,
        },
        tooltip: {
          custom: ({ seriesIndex, dataPointIndex, w }) => {
            const data = w.config.series[seriesIndex].data[dataPointIndex]
            return `
              <div class="p-75">
                <div class="font-weight-bold mb-25">${data.label}</div>
                <div class="text-muted small">${data.timeRange}</div>
                <div class="text-muted small">${data.durationText}</div>
              </div>
            `
          },
        },
        legend: {
          show: false,
        },
      }
    },
    timelineChartSeries() {
      const today = moment().startOf('day')
      const series = []

      this.timelineSegments.forEach((segment, index) => {
        const startMinutes = this.timelineSegments
          .slice(0, index)
          .reduce((sum, seg) => sum + (seg.duration * this.shiftDuration * 60) / 100, 0)

        const duration = (segment.duration * this.shiftDuration * 60) / 100
        const start = today.clone().add(startMinutes, 'minutes').valueOf()
        const end = today.clone().add(startMinutes + duration, 'minutes').valueOf()

        series.push({
          x: 'Turno',
          y: [start, end],
          fillColor: segment.color,
          label: segment.label,
          timeRange: segment.time,
          durationText: `${Math.round(duration)} min (${segment.duration}%)`,
        })
      })

      return [{
        data: series,
      }]
    },
    currentListDetails() {
      if (!this.device.list?.list) {
        return {
          text: this.device.values,
          icon: this.device.icon,
          color: this.device.color
        }
      }

      const listItem = this.device.list.list.find(item => item.key === this.device.values)
      const defaultState = {
        text: this.device.list.default,
        icon: this.device.icon,
        color: this.device.color
      }
      if (!listItem) return defaultState

      const createdAt = moment(this.device.createdAt).utc();
      const expirationTime = createdAt.clone().add(listItem.time, 'minutes');
      const nowTime = moment(this.now).utc(); 

      if (nowTime.isSameOrBefore(expirationTime)) {
        return listItem;
      } else {
        return defaultState;
      }
    }
  },
  methods: {
    hexToRgb(hex) {
      hex = hex.replace('#', '')
      const bigint = parseInt(hex, 16)
      const r = (bigint >> 16) & 255
      const g = (bigint >> 8) & 255
      const b = (bigint & 255)
      return `${r},${g},${b}`
    },
    // SIMULATE FAILURE STATE FOR DEMO
    isInFailureState() {
      return false
    },
    refreshCharts() {
      // fix charts when collapse opens
      this.$nextTick(() => {
        if (this.$refs.radialChart && this.device.sensorType === 'demo') {
          this.$refs.radialChart.refresh()
        }
        if (this.$refs.timelineChart && this.device.sensorType === 'demo') {
          this.$refs.timelineChart.refresh()
        }
      })
    },
    openEditModal(showInterval) {
      this.showInterval = showInterval
      this.dialogEdit = true
    },
    handleEditSubmit(formData) {
      this.$emit('update', formData)
    },
    transformIotData(date, values) {
      const x = date
      const y = values
      return [{ x, y }]
    },
    findListDetailsByValue(value) {
    if (!this.device.list || !this.device.list.list) {
      return {
        text: value,
        icon: 'ActivityIcon',
        color: this.device.color,
      };
    }
    const item = this.device.list.list.find(item => item.key === value);
    return item ? item : { text: value, icon: 'ActivityIcon', color: this.device.color };
  },
  },
  mounted(){
    if(this.device.sensorType === 'list'){
      this.intervalId = setInterval(() => {
      this.now = moment().utc()
    }, 1000)
    }
  },
  beforeDestroy() {
    if (this.intervalId) clearInterval(this.intervalId);
  },
}
</script>

<style>
.card-data {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 1.5rem;
  text-align: center;
  gap: 1rem; /* Adjusted gap */
}

.card-data p {
  margin: 0;
  padding: 0;
  font-size: 1.5rem; /* Adjusted font size */
  font-weight: bolder;
}

.card-data .feather {
  width: 2rem; /* Adjusted icon size */
  height: 2rem; /* Adjusted icon size */
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
  fill: none;
}

.bigger-checkbox {
  transform: scale(1.5) !important;
}

.bigger-checkbox input {
  background-color: red !important;
}

.custom {
  height: 100%;
}

.custom-div-body {
  width: 100% !important;
}

.circle-badge-online {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  margin-right: 10px;
  background-color: #28c76f;
}

.circle-badge-offline {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  margin-right: 10px;
  background-color: #ea5455;
}

.device-values {
  font-size: 2rem; /* Adjusted font size */
  white-space: nowrap; /* Prevent text wrapping */
}

.device-value {
  font-size: 2.5rem; /* Increased font size for the value */
  font-weight: bold; /* Make the value bold */
  margin-right: 0.5rem; /* Add some space between value and unit */
}

.device-unit {
  font-size: 2rem; /* Slightly smaller font size for the unit */
  color: #6c757d; /* Secondary color */
}

.interval-date-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0 1rem;
}

.interval-time {
  margin: 0;
  padding: 0;
  font-size: 1rem;
}

.interval-date {
  margin: 0;
  padding: 0;
  font-size: 1rem;
}

@media (max-width: 767px) {
  .demo-device-container {
    flex-direction: column !important;
    padding: 1rem !important;
    margin-left: 0 !important;
  }
  .demo-device-container .d-flex.flex-column {
    margin-right: 0 !important;
    margin-bottom: 1rem !important;
    width: 100% !important;
  }
  .demo-device-container .d-flex.flex-column:last-child {
    margin-bottom: 0 !important;
  }
  .demo-device-container .d-flex.flex-column[style*="width: 75%"] {
    width: 100% !important;
  }
  .demo-device-container .vue-apex-charts {
    height: 250px !important;
  }
  .demo-device-container .vue-apex-charts[ref="timelineChart"] {
    height: 150px !important;
  }
}

@media (min-width: 768px) {
  .text-md-left {
    text-align: left !important;
  }
  .w-md-auto {
    width: auto !important;
  }
  .justify-content-between {
    justify-content: space-between !important;
  }
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/map-leaflet.scss';
@import '@core/scss/vue/libs/chart-apex.scss';
</style>
